/**
 * pointGeometry — pure 2D point/polygon primitives shared across the hit-test
 * and pick pipelines (ONE truth).
 *
 * These are the canonical implementations of the ray-cast point-in-polygon, the
 * point-to-segment distance, and the nearest-distance-to-a-closed-polyline. They
 * were historically copied between the SVG hit-test (`apps/web/.../hitTest.ts`),
 * the Three.js renderer pick (`apps/web/.../scene3d/raycast/distance.ts`) and —
 * most recently — the HATCH pick (`hatchBoundaryGeometry.ts`). The third copy
 * tripped the jscpd clone gate; this leaf module collapses them to one source.
 *
 * Pure CAD math owned by `drawing-model` (precedent: `splineMath.ts`,
 * `hatchBoundaryGeometry.ts`). No three / no apps/web import — both render pipes
 * (which already depend on `drawing-model`) import these without a back-edge.
 */

/** Minimal 2D point. Field-compatible with the scene `Vec2` and the
 *  `hatchBoundaryGeometry` `Point2` (both `{ x, y }`). */
export interface Point2D {
  x: number;
  y: number;
}

/**
 * Standard ray-cast (even-odd rule) point-in-polygon test. Returns `false` for
 * a degenerate polygon (fewer than 3 vertices). The polygon is treated as a
 * CLOSED ring (the last→first edge is implicit). Verbatim algorithm shared by
 * the SVG / Three.js hit-tests and the HATCH pick.
 */
export function pointInPolygon(
  p: Point2D,
  polygon: readonly Point2D[],
): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const xi = polygon[i]!.x;
    const yi = polygon[i]!.y;
    const xj = polygon[j]!.x;
    const yj = polygon[j]!.y;
    const intersects =
      yi > p.y !== yj > p.y &&
      p.x < ((xj - xi) * (p.y - yi)) / (yj - yi + Number.EPSILON) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/**
 * Distance from a point to a finite line segment `a`→`b` in 2D (XY plane;
 * any z is ignored). Degenerate segment (a==b) → distance to the point.
 */
export function distanceToSegment2D(
  p: Point2D,
  a: Point2D,
  b: Point2D,
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return Math.hypot(p.x - a.x, p.y - a.y);
  }
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/**
 * Nearest distance from `p` to a CLOSED polyline ring (`polygon`). Walks every
 * edge incl. the implicit last→first closing edge. Returns `Infinity` for a
 * polygon with fewer than 2 vertices.
 */
export function distanceToClosedPolyline(
  p: Point2D,
  polygon: readonly Point2D[],
): number {
  if (polygon.length < 2) return Infinity;
  let min = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i]!;
    const b = polygon[(i + 1) % polygon.length]!;
    const d = distanceToSegment2D(p, a, b);
    if (d < min) min = d;
  }
  return min;
}

/** One boundary loop of a multi-island filled region: a vertex ring plus a
 *  hole flag (`isHole:false` = exterior contour, `isHole:true` = interior cut).
 *  Field-compatible with the renderer's `TessellationLoopInput` and the pick's
 *  loop shape so both feed {@link isPointInFilledRegion} the same data. */
export interface FilledRegionLoop {
  readonly vertices: readonly Point2D[];
  readonly isHole: boolean;
}

/**
 * ONE-TRUTH hole→exterior ownership shared by the HATCH render tessellator
 * (`scene3d/hatch/earcutLoops.tessellateHatchLoops`) and the canvas/HATCHEDIT
 * pick predicate ({@link isPointInFilledRegion}). For each hole it returns the
 * index (into `exteriors`) of the exterior that SUBTRACTS it; a hole is owned by
 * AT MOST one exterior so the renderer and predicate can never diverge on which
 * region a hole carves (the PR #410 P2: a hole inside two overlapping/nested
 * exteriors was carved by the predicate from EVERY containing exterior but
 * subtracted by the tessellator from only one).
 *
 * Two ownership regimes — matching the tessellator's two regimes exactly:
 *   - SINGLE exterior (incl. the no-exterior fallback's synthetic exterior):
 *     the tessellator's `tessellateOneRegion` subtracts ALL holes from that one
 *     exterior UNCONDITIONALLY (no containment test). So every hole is owned by
 *     exterior 0 here, regardless of where its representative vertex lies.
 *   - MULTIPLE exteriors: each hole is owned by the FIRST exterior whose polygon
 *     CONTAINS the hole's representative (first) vertex, or `-1` if NONE contains
 *     it (unowned → DROPPED, carves no region) — the multi-island assignment.
 *
 * `exteriors` and `holes` are the already-usable (≥3-vertex) loops; the caller
 * partitions and filters them. Returns one entry per hole, index-aligned to
 * `holes`.
 */
export function assignHolesToExteriors(
  exteriors: readonly FilledRegionLoop[],
  holes: readonly FilledRegionLoop[],
): number[] {
  // Single-region regime (one exterior): every hole is subtracted from it
  // unconditionally — byte-identical to `tessellateOneRegion`'s behaviour.
  if (exteriors.length <= 1) {
    return holes.map(() => (exteriors.length === 1 ? 0 : -1));
  }
  // Multi-island regime: first containing exterior, else unowned (-1).
  return holes.map((hole) => {
    const probe = hole.vertices[0];
    if (!probe) return -1;
    for (let e = 0; e < exteriors.length; e++) {
      if (pointInPolygon(probe, exteriors[e]!.vertices)) return e;
    }
    return -1;
  });
}

/**
 * ONE-TRUTH "which boundary loops does the renderer actually emit?" — the loop
 * set whose edges form the VISIBLE fill boundary, mirroring `tessellateHatchLoops`
 * exactly: every exterior, PLUS each hole OWNED by some exterior. An UNOWNED hole
 * (no exterior contains its representative vertex, multi-island regime) is DROPPED
 * by the tessellator and carves nothing — so it is NOT a fill edge and must be
 * excluded from a nearest-edge pick measurement (PR #410 P3: a click near an
 * invisible orphan loop must not register as near the hatch).
 *
 * Returns the INDICES (into `usableLoops`) of the rendered loops, so a caller can
 * map back to its own richer loop objects (e.g. ones that also carry bulges) and
 * measure distance however it needs. Shared by the canvas pick
 * (`scene3d/raycast/distance.ts`) and the HATCHEDIT pick
 * (`distanceToHatchBoundary`) so both measure the SAME loop set the renderer
 * draws.
 *
 * No-exterior fallback (legacy parity): when NO loop is tagged exterior but ≥1
 * usable loop exists, the FIRST usable loop is the synthetic exterior and the
 * rest are its (all-owned) holes — same fallback the tessellator uses. `usableLoops`
 * must already be the ≥3-vertex subset (the caller filters degenerate loops).
 *
 * Tagged-degenerate suppression (PR #410 P3) — `hadTaggedExterior` reports whether
 * the RAW (pre-degenerate-filter) input tagged ANY exterior loop. When an exterior
 * WAS tagged but degenerated away so no usable exterior survives, the tessellator
 * fills NOTHING and promotes no interior loop — so there are NO rendered loops and
 * a nearest-edge measurement must see an EMPTY set (→ Infinity), never an interior
 * loop promoted to a synthetic exterior. This mirrors `isPointInFilledRegion`'s
 * `hadTaggedExterior` gate exactly, so the rendered-loop set and the filled-region
 * predicate agree cell-for-cell. Defaults to `false` (no tag) for the genuine
 * untagged-import path, which keeps the first-usable fallback.
 */
export function renderedBoundaryLoopIndices(
  usableLoops: readonly FilledRegionLoop[],
  hadTaggedExterior = false,
): number[] {
  if (usableLoops.length === 0) return [];
  const exteriorIdx: number[] = [];
  const holeIdx: number[] = [];
  for (let i = 0; i < usableLoops.length; i++) {
    if (usableLoops[i]!.isHole) holeIdx.push(i);
    else exteriorIdx.push(i);
  }
  // Tagged-but-degenerate exterior: a hatch that tagged an exterior whose loop
  // degenerated away (no usable exterior) renders NOTHING — never promote an
  // interior loop to a synthetic exterior. Mirrors `isPointInFilledRegion`'s gate
  // + `tessellateHatchLoops`'s `hadTaggedExterior` short-circuit (PR #410 P3).
  if (exteriorIdx.length === 0 && hadTaggedExterior) return [];
  // No-exterior fallback: index 0 is the synthetic exterior, the rest are holes.
  const effExteriorIdx = exteriorIdx.length === 0 ? [0] : exteriorIdx;
  const effHoleIdx =
    exteriorIdx.length === 0
      ? usableLoops.map((_, i) => i).slice(1)
      : holeIdx;

  const owners = assignHolesToExteriors(
    effExteriorIdx.map((i) => ({ vertices: usableLoops[i]!.vertices, isHole: false })),
    effHoleIdx.map((i) => ({ vertices: usableLoops[i]!.vertices, isHole: true })),
  );
  const rendered: number[] = [...effExteriorIdx];
  for (let h = 0; h < effHoleIdx.length; h++) {
    if (owners[h]! >= 0) rendered.push(effHoleIdx[h]!); // skip dropped orphans
  }
  return rendered;
}

/**
 * ONE-TRUTH filled-region predicate shared by the HATCH render tessellator
 * (`scene3d/hatch/earcutLoops.tessellateHatchLoops`) and the canvas/HATCHEDIT
 * picks (`scene3d/raycast/distance.ts` + `distanceToHatchBoundary`). A point is
 * in a hatch's FILLED area iff it lies inside SOME exterior loop AND NOT inside
 * any HOLE that is OWNED-BY that same exterior region — exactly the area the
 * tessellator fills (each exterior minus the holes {@link assignHolesToExteriors}
 * assigns to it).
 *
 * Definition (matches the renderer's per-region earcut-with-holes pass):
 *   filled(p) ⇔ ∃ exterior E with p ∈ E and ∀ hole H OWNED-BY E: p ∉ H.
 * Ownership is {@link assignHolesToExteriors}. With MULTIPLE exteriors, a hole
 * owned by a DIFFERENT exterior — even one that geometrically contains p — does
 * NOT exclude p from E, because the tessellator subtracts that hole only from
 * its owner, so E still fills there. A hole contained by NO exterior is unowned
 * (owner index -1) and carves nothing (the tessellator DROPS it).
 *
 * No-exterior fallback (legacy parity): when NO loop is tagged exterior but ≥1
 * usable loop exists, the FIRST usable loop acts as the exterior and the rest as
 * its holes (all owned by it) — same fallback the tessellator and pick used
 * before multi-island.
 *
 * Degenerate loops (<3 vertices) are ignored (they bound no area). Returns
 * `false` when there is no usable loop at all.
 */
export function isPointInFilledRegion(
  p: Point2D,
  loops: readonly FilledRegionLoop[],
): boolean {
  // Did the hatch TAG any exterior loop (`isHole === false`) in the RAW input —
  // BEFORE the degenerate-skip below? When a tagged exterior DEGENERATED (<3
  // verts) leaving no usable exterior, the hatch has no valid fill contour and
  // must read EMPTY — NOT promote an interior loop to a synthetic exterior. Only
  // a hatch with NO tagged exterior at all (untagged import) earns the first-loop
  // fallback. Mirrors `tessellateHatchLoops`'s `hadTaggedExterior` gate so the
  // pick predicate and the render tessellator agree cell-for-cell (PR #410 P3).
  const hadTaggedExterior = loops.some((l) => !l.isHole);

  const usable = loops.filter((l) => l.vertices.length >= 3);
  if (usable.length === 0) return false;

  const exteriors = usable.filter((l) => !l.isHole);
  const holes = usable.filter((l) => l.isHole);

  // No USABLE exterior:
  //   - a tagged-but-degenerate exterior → no fill contour → empty (return false).
  //   - genuinely untagged (no exterior tag at all) → first-usable-loop fallback:
  //     the FIRST usable loop is the synthetic exterior, the rest its holes (all
  //     owned by it), sharing `assignHolesToExteriors`'s single-region regime.
  if (exteriors.length === 0 && hadTaggedExterior) return false;
  const effectiveExteriors = exteriors.length === 0 ? [usable[0]!] : exteriors;
  const effectiveHoles = exteriors.length === 0 ? usable.slice(1) : holes;

  // ONE-TRUTH ownership — identical assignment the tessellator subtracts with.
  const owners = assignHolesToExteriors(effectiveExteriors, effectiveHoles);

  // Inside some exterior E AND not inside one of E's OWNED holes. A hole owned by
  // a different exterior never carves E even if it geometrically contains p.
  for (let e = 0; e < effectiveExteriors.length; e++) {
    if (!pointInPolygon(p, effectiveExteriors[e]!.vertices)) continue;
    let inHole = false;
    for (let h = 0; h < effectiveHoles.length; h++) {
      if (owners[h] !== e) continue; // only E's own holes carve E
      if (pointInPolygon(p, effectiveHoles[h]!.vertices)) {
        inHole = true;
        break;
      }
    }
    if (!inHole) return true;
  }
  return false;
}
